import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Row, UncontrolledTooltip } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "src/common/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../slices/store";
import { setChartServedImp } from "src/slices/Filters/reducer";
import { GiEmptyHourglass } from "react-icons/gi";
import { FiTable } from "react-icons/fi";
import UnderlyingdataModal from "./UnderlyingdataModal";
import { isKPIValueZero, getZeroValueMessage } from "src/helpers/analyticsHelper";
import {
    WidgetData,
    WidgetConfig,
    ChartPoint,
    FetchParams,
    METRICS_TITLES,
    WIDGET_CONFIG,
    DEFAULT_METRICS_WIDGETS,
    DEFAULT_RATES_WIDGETS,
    mapTitle,
    getFormattedValue,
    getDataByKey,
    getChartData,
    getTrendColor,
    createBarChartOptions,
    createBarFallbackOptions,
    createAreaOptions,
    getRowLayout,
    getColWidth,
} from "./widgetUtils";

// ─── WidgetCard (Memoized Sub-Component) ─────────────────────────────

interface WidgetCardProps {
    widget: WidgetData;
    uniqueKey: string;
    loading: boolean;
    noDataAvailable: boolean;
    dataBy: string;
    xl: number;
    onOpenModal: (widget: WidgetData) => void;
    tooltipFormatter: (value: number, opts: any) => string;
}

const WidgetCard = React.memo(({
    widget,
    uniqueKey,
    loading,
    noDataAvailable,
    dataBy,
    xl,
    onOpenModal,
    tooltipFormatter,
}: WidgetCardProps) => {
    const displayTitle = mapTitle(widget.title);
    const config: WidgetConfig = WIDGET_CONFIG[displayTitle] || WIDGET_CONFIG[widget.title] || {
        displayChartType: 'area', chartColor: '#038edc', invertColor: false, barFallbackColor: '#6e6fef',
    };

    const isIncrease = widget.subtitle.includes("+");
    const isDecrease = widget.subtitle.includes("-");
    const trendColor = getTrendColor(isIncrease, config.invertColor);

    const numericValue = parseFloat(widget.price.replace(/[^0-9.-]/g, '')) || 0;
    const dataKey = getDataByKey(dataBy) as keyof WidgetData;
    const dataPoints = (widget[dataKey] as Array<{ date: string; value: number }>) || [];
    const chartData: ChartPoint[] = getChartData(dataPoints);

    return (
        <Col xl={xl} sm={6} key={uniqueKey} className="d-flex">
            <Card className="position-relative flex-fill analytics-card" style={{ borderRadius: "6px" }}>
                <CardBody>
                    {/* Widget title */}
                    <div className="d-flex flex-column position-relative">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <h6 className="text-uppercase mb-0" style={{ fontSize: '0.75rem' }}>
                                {displayTitle}
                            </h6>
                            {!loading && noDataAvailable && numericValue > 0 && (
                                <div
                                    className="underlying-data-icon"
                                    onClick={() => onOpenModal(widget)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    title="View underlying data"
                                >
                                    {(FiTable as unknown as React.FC<any>)({ size: 16, color: '#6c757d' })}
                                </div>
                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-indicator" style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <div className="loading-hourglass">
                                {(GiEmptyHourglass as unknown as React.FC<any>)({ size: 24 })}
                            </div>
                            <div className="loading-text text-center text-muted mt-3">
                                <h6>Fetching data...</h6>
                                <p className="small">Please wait while we load your analytics.</p>
                            </div>
                        </div>
                    ) : !noDataAvailable ? (
                        <div className="loading-indicator" style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <div>
                                {(GiEmptyHourglass as unknown as React.FC<any>)({ size: 24 })}
                            </div>
                            <div className="loading-text text-center text-muted mt-3">
                                <h6>No data found for applied filters.</h6>
                                <p className="small">Please change filters to see data.</p>
                            </div>
                        </div>
                    ) : isKPIValueZero(widget) ? (
                        <div className="loading-indicator" style={{ height: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <div>
                                {(GiEmptyHourglass as unknown as React.FC<any>)({ size: 24 })}
                            </div>
                            <div className="loading-text text-center text-muted mt-3">
                                <h6>{getZeroValueMessage(displayTitle).heading}</h6>
                                <p className="small">{getZeroValueMessage(displayTitle).description}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="d-flex flex-column">
                                {/* Main value and trend */}
                                <div className="d-flex justify-content-between align-items-start mb-1 mt-3">
                                    <h4 className="mb-0 metric-value" style={{ fontWeight: 'bold' }}>
                                        {getFormattedValue(numericValue, widget.title)}
                                    </h4>
                                    <div className="text-end d-flex flex-column align-items-end" style={{ gap: '4px' }}>
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                            {widget.trendDate}
                                        </small>
                                        {/* Delta (Δ) display */}
                                        <small
                                            id={`delta-tooltip-${uniqueKey}`}
                                            style={{
                                                color: trendColor.text,
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            {'Δ '}
                                            {isDecrease ? `-${widget.diff}` : widget.diff}
                                        </small>
                                        <UncontrolledTooltip
                                            placement="top"
                                            target={`delta-tooltip-${uniqueKey}`}
                                            innerClassName="custom-tooltip-inner"
                                        >
                                            The raw change in value compared to the previous period. Shows how much the number increased or decreased.
                                        </UncontrolledTooltip>
                                        {/* Trend badge with arrow */}
                                        <span
                                            id={`trend-tooltip-${uniqueKey}`}
                                            className="d-inline-block px-1 rounded"
                                            style={{
                                                backgroundColor: trendColor.bg,
                                                color: trendColor.text,
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                alignSelf: 'flex-end',
                                            }}
                                        >
                                            {isIncrease ? '↑ ' : isDecrease ? '↓ ' : ''}
                                            {widget.subtitle.replace("+", "").replace("-", "")}
                                        </span>
                                        <UncontrolledTooltip
                                            placement="top"
                                            target={`trend-tooltip-${uniqueKey}`}
                                            innerClassName="custom-tooltip-inner"
                                        >
                                            The percentage change compared to the previous period. Shows how much the value grew or declined relative to its earlier level.
                                        </UncontrolledTooltip>
                                    </div>
                                </div>

                                {/* Charts */}
                                <div className="apex-charts">
                                    {config.displayChartType === 'bar' ? (
                                        <ReactApexChart
                                            options={createBarChartOptions(
                                                config.chartColor,
                                                chartData.map((item) => item.x),
                                                (value: number) => getFormattedValue(value, widget.title)
                                            )}
                                            series={[{
                                                name: widget.title || "Data Series",
                                                data: chartData.map((item) => item.y),
                                            }]}
                                            type="bar"
                                            height={130}
                                        />
                                    ) : (
                                        <ReactApexChart
                                            options={{
                                                ...(widget.charttype === "bar"
                                                    ? createBarFallbackOptions(config.barFallbackColor, tooltipFormatter)
                                                    : createAreaOptions(tooltipFormatter)),
                                                markers: {
                                                    size: dataPoints.length <= 2 ? 4 : 0,
                                                },
                                            }}
                                            series={[{
                                                name: widget.title || "Data Series",
                                                data: chartData,
                                            }]}
                                            type={widget.charttype as any}
                                            className="apex-charts"
                                            dir="ltr"
                                            height="130"
                                        />
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
});

WidgetCard.displayName = 'WidgetCard';

// ─── AnalyticsWidgets (Main Component) ────────────────────────────────

interface AnalyticsWidgetsProps {
    setNoData: (value: boolean) => void;
}

const AnalyticsWidgets: React.FC<AnalyticsWidgetsProps> = ({ setNoData }) => {
    // Spend/volume metrics state (Est. Spend, Impressions, Reach, Clicks)
    const [metricsData, setMetricsData] = useState<WidgetData[]>([]);
    const [metricsNoData, setMetricsNoData] = useState(false);
    const [metricsLoading, setMetricsLoading] = useState(true);
    const metricsFetchId = useRef(0);

    // Rate metrics state (AVG. CTR, CPC, Conv. Rate, CPA)
    const [ratesData, setRatesData] = useState<WidgetData[]>([]);
    const [ratesNoData, setRatesNoData] = useState(false);
    const [ratesLoading, setRatesLoading] = useState(true);
    const ratesFetchId = useRef(0);

    // Shared modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedKPI, setSelectedKPI] = useState<{ title: string; data: Array<{ date: string; value: number }> } | null>(null);

    const dispatch = useDispatch();
    const filters = useSelector((state: RootState) => state.filters);
    const { dataBy, isTopNavReady } = filters;
    const userState = useSelector((state: RootState) => state.login);

    // Memoized tooltip formatter for area/barFallback charts
    const tooltipFormatter = useCallback((value: number, opts: any) => {
        const title = opts.w.config.series[opts.seriesIndex].name || "";
        return getFormattedValue(value, title);
    }, []);

    // Modal handlers
    const handleOpenModal = useCallback((widget: WidgetData) => {
        const dataKey = getDataByKey(dataBy);
        setSelectedKPI({
            title: mapTitle(widget.title),
            data: (widget as any)[dataKey] || [],
        });
        setModalOpen(true);
    }, [dataBy]);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
        setSelectedKPI(null);
    }, []);

    // Fetch spend/volume metrics — /minichart-data
    const fetchMetrics = useCallback(async (params: FetchParams, requestId: number) => {
        try {
            const response = await axiosInstance.get('/minichart-data', { params });

            // Side effect: dispatch served impressions
            if (response.data && response.data[0]) {
                dispatch(setChartServedImp(parseInt(response.data[0].price.replace(/,/g, ''), 10)));
            }

            const hasData = response.data.some((widget: any) => {
                const price = parseFloat(widget.price.replace(/[^0-9.-]/g, '')) || 0;
                return price > 0;
            });

            if (requestId === metricsFetchId.current) {
                setMetricsNoData(hasData);
                setMetricsData(response.data);
                setMetricsLoading(false);
            }

            // Side effect: setNoData callback with 3s timeout
            setNoData(!hasData);
            const timer = setTimeout(() => {
                setNoData(false);
            }, 3000);
            return () => clearTimeout(timer);
        } catch (error) {
            console.error("Error fetching metrics data:", error);
            if (requestId === metricsFetchId.current) {
                setMetricsLoading(false);
                setMetricsNoData(false);
            }
        }
    }, [dispatch, setNoData]);

    // Fetch rate metrics — /minichartslidesecond-data
    const fetchRates = useCallback(async (params: FetchParams, requestId: number) => {
        try {
            const response = await axiosInstance.get('/minichartslidesecond-data', { params });

            const hasData = response.data.some((widget: any) => {
                const price = parseFloat(widget.price.replace(/[^0-9.-]/g, '')) || 0;
                return price > 0;
            });

            if (requestId === ratesFetchId.current) {
                setRatesNoData(hasData);
                setRatesData(response.data as WidgetData[]);
                setRatesLoading(false);
            }
        } catch (error) {
            console.error("Error fetching rates data:", error);
            if (requestId === ratesFetchId.current) {
                setRatesLoading(false);
                setRatesNoData(false);
            }
        }
    }, []);

    // Permission filter: Client users may have a subset of widgets
    const analyticsWidgetsRaw = userState.user?.user?.analytics_widgets as string | undefined;
    const allowedWidgets = useMemo(() =>
        analyticsWidgetsRaw ? (JSON.parse(analyticsWidgetsRaw) as string[]) : null,
        [analyticsWidgetsRaw]
    );

    // Data fetching effect
    useEffect(() => {
        if (!isTopNavReady) {
            setMetricsLoading(true);
            setRatesLoading(true);
            return;
        }

        setMetricsLoading(true);
        setRatesLoading(true);

        const params: FetchParams = {
            id: filters.selectedOrder,
            start_date: filters.startDate,
            end_date: filters.endDate,
            lineItems: filters.selectedLineItems,
            mediaType: filters.selectedMediaType,
            rateType: filters.selectedRateType,
            exchangeType: filters.selectedExchangeType,
            adFormat: filters.selectedAdFormat,
            users_id: userState.user?.user?.users_id,
            allowedWidgets: allowedWidgets ? JSON.stringify(allowedWidgets) : undefined,
        };

        if (filters.selectedOrder && filters.selectedLineItems && filters.selectedLineItems.trim() !== '') {
            const metricsRequestId = ++metricsFetchId.current;
            const ratesRequestId = ++ratesFetchId.current;
            fetchMetrics(params, metricsRequestId);
            fetchRates(params, ratesRequestId);
        } else {
            setMetricsData([]);
            setMetricsNoData(false);
            setMetricsLoading(false);
            setRatesData([]);
            setRatesNoData(false);
            setRatesLoading(false);
        }
    }, [isTopNavReady, filters.selectedLineItems, filters.startDate, filters.endDate,
        filters.selectedMediaType, filters.selectedRateType, filters.selectedExchangeType,
        filters.selectedAdFormat, dataBy, userState, allowedWidgets]);

    // Filtered default widgets based on permissions
    const filteredDefaultMetrics = useMemo(() => {
        if (!allowedWidgets || !Array.isArray(allowedWidgets)) return DEFAULT_METRICS_WIDGETS;
        return DEFAULT_METRICS_WIDGETS.filter(w => allowedWidgets.includes(w.title));
    }, [allowedWidgets]);

    const filteredDefaultRates = useMemo(() => {
        if (!allowedWidgets || !Array.isArray(allowedWidgets)) return DEFAULT_RATES_WIDGETS;
        return DEFAULT_RATES_WIDGETS.filter(w => allowedWidgets.includes(w.title));
    }, [allowedWidgets]);

    // Memoized widget lists
    const metricsWidgets = useMemo(() =>
        metricsData.length > 0 ? metricsData : filteredDefaultMetrics,
        [metricsData, filteredDefaultMetrics]
    );
    const ratesWidgets = useMemo(() =>
        ratesData.length > 0 ? ratesData : filteredDefaultRates,
        [ratesData, filteredDefaultRates]
    );

    // Merge into single grid in canonical order, filtered by permission
    const allWidgets = useMemo(() => {
        const merged = [...metricsWidgets, ...ratesWidgets];
        if (!allowedWidgets || !Array.isArray(allowedWidgets)) return merged;
        return merged.filter((w: WidgetData) => allowedWidgets.includes(w.title));
    }, [metricsWidgets, ratesWidgets, allowedWidgets]);

    // Adaptive layout
    const rowSizes = useMemo(() => getRowLayout(allWidgets.length), [allWidgets.length]);

    // Build rows from allWidgets
    const rows = useMemo(() => {
        const result: Array<{ widgets: WidgetData[]; colWidth: number }> = [];
        let idx = 0;
        for (const size of rowSizes) {
            result.push({
                widgets: allWidgets.slice(idx, idx + size),
                colWidth: getColWidth(size),
            });
            idx += size;
        }
        return result;
    }, [allWidgets, rowSizes]);

    return (
        <React.Fragment>
            {rows.map((row, rowIdx) => (
                <Row key={rowIdx}>
                    {row.widgets.map((widget, i) => {
                        const isMetricsWidget = METRICS_TITLES.includes(widget.title);
                        const loading = isMetricsWidget ? metricsLoading : ratesLoading;
                        const noDataAvailable = isMetricsWidget ? metricsNoData : ratesNoData;
                        const slugTitle = widget.title.toLowerCase().replace(/[^a-z0-9]/g, '-');
                        const uniqueKey = `widget-${slugTitle}`;

                        return (
                            <WidgetCard
                                key={uniqueKey}
                                widget={widget}
                                uniqueKey={uniqueKey}
                                loading={loading}
                                noDataAvailable={noDataAvailable}
                                dataBy={dataBy}
                                xl={row.colWidth}
                                onOpenModal={handleOpenModal}
                                tooltipFormatter={tooltipFormatter}
                            />
                        );
                    })}
                </Row>
            ))}

            {/* Underlying Data Modal */}
            {selectedKPI && (
                <UnderlyingdataModal
                    isOpen={modalOpen}
                    toggle={handleCloseModal}
                    kpiData={selectedKPI.data || []}
                    dataBy={dataBy}
                    columns={[
                        {
                            header: dataBy === 'day' ? 'Date' : dataBy === 'week' ? 'Week' : dataBy === 'month' ? 'Month' : dataBy === 'quarter' ? 'Quarter' : 'Year',
                            accessor: 'date',
                        },
                        {
                            header: selectedKPI.title,
                            accessor: 'value',
                            formatter: (value: any) => {
                                if (typeof value === 'string' && value.includes('%')) {
                                    return value;
                                }
                                const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
                                return getFormattedValue(numValue, selectedKPI.title);
                            },
                        },
                    ]}
                    title={selectedKPI.title}
                />
            )}
        </React.Fragment>
    );
};

export default AnalyticsWidgets;
